/**
 * Public Vendor Onboarding Form — rendered at /admin/vendor-onboarding/:token
 * with NO authentication. Vendors open the link, fill the form, submit.
 *
 * No internal DESCON data is loaded here — only the safe public-view of the
 * invitation (status + expiry + hints) and the form-meta vendor type list.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Buildings, Warning, CheckCircle, FileArrowUp, Trash,
  Clock, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vendor-onboarding/public`;

const VENDOR_TYPE_LABELS = {
  supplier: "Supplier (goods / materials)",
  subcontractor: "Subcontractor",
  consultant: "Consultant",
  service_provider: "Service provider",
  rental_vendor: "Rental vendor",
  calibration_provider: "Calibration provider",
  logistics_provider: "Logistics / freight provider",
  maintenance_vendor: "Maintenance vendor",
  other: "Other",
};

const EMPTY = {
  company_name: "", vendor_type: "supplier", category: "",
  services_summary: "",
  contact_person: "", phone: "", email: "",
  address: "", country: "", region: "",
  registration_no: "", tax_id: "",
  bank_name: "", bank_account: "", swift_iban: "",
  mobile_money_provider: "", mobile_money_number: "",
  company_profile: "", past_experience: "", references: "",
  declaration_accepted: false,
  documents: [],
};

export default function VendorOnboardingPublic() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | active | inactive | submitted | error
  const [invitation, setInvitation] = useState(null);
  const [meta, setMeta] = useState({ vendor_types: [], max_doc_bytes: 5 * 1024 * 1024, max_doc_count: 5 });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/${token}`);
        if (!alive) return;
        setInvitation(data.invitation);
        setMeta(data.form_meta || meta);
        setStatus(data.ok ? "active" : (data.invitation?.status || "inactive"));
        if (data.invitation?.vendor_name_hint) {
          setForm((f) => ({ ...f, company_name: data.invitation.vendor_name_hint, email: data.invitation.contact_email_hint || "" }));
        }
      } catch (e) {
        if (!alive) return;
        setStatus("error");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleFiles = async (files) => {
    const existing = form.documents || [];
    const remaining = (meta.max_doc_count || 5) - existing.length;
    const incoming = Array.from(files).slice(0, remaining);
    const added = [];
    for (const file of incoming) {
      if (file.size > (meta.max_doc_bytes || 5 * 1024 * 1024)) {
        toast.error(`"${file.name}" exceeds the 5 MB limit and was skipped.`);
        continue;
      }
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(",")[1] || "";
      added.push({ name: file.name, content_type: file.type, type: "supporting", data_base64: base64, size_bytes: file.size });
    }
    setForm((s) => ({ ...s, documents: [...existing, ...added] }));
  };

  const removeDoc = (i) => setForm((s) => ({ ...s, documents: s.documents.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.company_name || form.company_name.length < 2) return toast.error("Company name is required");
    if (!form.declaration_accepted) return toast.error("Please accept the declaration");
    setBusy(true);
    try {
      const payload = {
        ...form,
        documents: form.documents.map((d) => ({
          name: d.name, type: d.type, content_type: d.content_type, data_base64: d.data_base64,
        })),
      };
      await axios.post(`${API}/${token}/submit`, payload);
      setStatus("submitted_now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ── States: error / expired / revoked / already submitted ─────────────
  if (status === "loading") {
    return <CenterCard><div className="text-sm text-slate-500">Loading onboarding link…</div></CenterCard>;
  }
  if (status === "error") {
    return (
      <CenterCard>
        <Warning size={36} weight="bold" className="text-rose-500 mx-auto" />
        <h1 className="font-display text-xl font-semibold mt-3">Onboarding link not found</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          This link is invalid. Please contact DESCON Procurement to request a new onboarding link.
        </p>
      </CenterCard>
    );
  }
  if (status === "expired") {
    return (
      <CenterCard>
        <Clock size={36} className="text-amber-500 mx-auto" />
        <h1 className="font-display text-xl font-semibold mt-3">This onboarding link has expired</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          Onboarding links are valid for 7 days. Please contact DESCON Procurement for a new link.
        </p>
      </CenterCard>
    );
  }
  if (status === "revoked") {
    return (
      <CenterCard>
        <Lock size={36} className="text-slate-500 mx-auto" />
        <h1 className="font-display text-xl font-semibold mt-3">This onboarding link has been revoked</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          DESCON Procurement revoked this invitation. Please contact them for a new link.
        </p>
      </CenterCard>
    );
  }
  if (status === "submitted" || status === "submitted_now") {
    return (
      <CenterCard>
        <CheckCircle size={36} weight="fill" className="text-emerald-500 mx-auto" />
        <h1 className="font-display text-xl font-semibold mt-3">Submission received</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          Thank you. DESCON Procurement will review your registration and get back to you. You may close this page.
        </p>
      </CenterCard>
    );
  }
  if (status === "inactive") {
    return (
      <CenterCard>
        <Warning size={36} className="text-amber-500 mx-auto" />
        <h1 className="font-display text-xl font-semibold mt-3">This onboarding link is no longer active</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-md">
          Please contact DESCON Procurement for assistance.
        </p>
      </CenterCard>
    );
  }

  // ── Active form ────────────────────────────────────────────────────────
  const docs = form.documents || [];
  const exp = invitation?.expires_at ? new Date(invitation.expires_at) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Brand header */}
      <header className="bg-[#0A2342] text-white">
        <div className="mx-auto max-w-3xl px-5 py-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded bg-white/10 flex items-center justify-center">
            <Buildings size={22} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">DESCON Group</div>
            <div className="text-[11px] uppercase tracking-widest text-white/70">Vendor / Supplier Onboarding</div>
          </div>
          <div className="ml-auto text-xs text-white/80 text-right">
            {exp && <div>Link expires: <span className="font-mono-alt">{exp.toLocaleDateString()}</span></div>}
            <div className="flex items-center gap-1 justify-end mt-0.5"><ShieldCheck size={12} /> Secure submission</div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-8 space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h1 className="font-display text-2xl font-bold text-slate-900">Register your company with DESCON</h1>
          <p className="text-sm text-slate-600 mt-2">
            Please complete the form below. Fields marked <span className="text-rose-600">*</span> are required.
            DESCON Procurement will review your submission and contact you within a few business days.
          </p>
        </div>

        {/* Company */}
        <FormCard title="Company information">
          <Row>
            <Field label="Company name" required>
              <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="vo-company-name" />
            </Field>
            <Field label="Vendor type" required>
              <Select value={form.vendor_type} onValueChange={(v) => set("vendor_type", v)}>
                <SelectTrigger data-testid="vo-vendor-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.vendor_types || Object.keys(VENDOR_TYPE_LABELS)).map((t) => (
                    <SelectItem key={t} value={t}>{VENDOR_TYPE_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Row>
          <Row>
            <Field label="Goods / services category">
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. PPE, surveying equipment, drilling services" data-testid="vo-category" />
            </Field>
          </Row>
          <Field label="Brief summary of goods / services offered">
            <Textarea rows={3} value={form.services_summary} onChange={(e) => set("services_summary", e.target.value)} data-testid="vo-services" />
          </Field>
        </FormCard>

        {/* Contact */}
        <FormCard title="Contact details">
          <Row>
            <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="vo-contact" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="vo-phone" /></Field>
          </Row>
          <Row>
            <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="vo-email" /></Field>
            <Field label="Country / region">
              <div className="flex gap-2">
                <Input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Country" data-testid="vo-country" />
                <Input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="City / region" data-testid="vo-region" />
              </div>
            </Field>
          </Row>
          <Field label="Address">
            <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="vo-address" />
          </Field>
        </FormCard>

        {/* Registration */}
        <FormCard title="Registration & tax">
          <Row>
            <Field label="Business registration no."><Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} data-testid="vo-reg-no" /></Field>
            <Field label="Tax / TIN no."><Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="vo-tax-id" /></Field>
          </Row>
        </FormCard>

        {/* Banking */}
        <FormCard title="Banking & payment details">
          <p className="text-xs text-slate-500 -mt-2 mb-2">Provide whatever applies. You can fill bank, mobile money, or both.</p>
          <Row>
            <Field label="Bank name"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="vo-bank-name" /></Field>
            <Field label="Account number"><Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} data-testid="vo-bank-acct" /></Field>
          </Row>
          <Row>
            <Field label="SWIFT / IBAN"><Input value={form.swift_iban} onChange={(e) => set("swift_iban", e.target.value)} data-testid="vo-swift" /></Field>
            <Field label="Mobile money provider"><Input value={form.mobile_money_provider} onChange={(e) => set("mobile_money_provider", e.target.value)} placeholder="EVC+, Zaad, M-Pesa…" data-testid="vo-mm-provider" /></Field>
          </Row>
          <Field label="Mobile money number"><Input value={form.mobile_money_number} onChange={(e) => set("mobile_money_number", e.target.value)} data-testid="vo-mm-number" /></Field>
        </FormCard>

        {/* Profile */}
        <FormCard title="Company profile & experience">
          <Field label="Company profile"><Textarea rows={3} value={form.company_profile} onChange={(e) => set("company_profile", e.target.value)} data-testid="vo-profile" /></Field>
          <Field label="Past experience"><Textarea rows={3} value={form.past_experience} onChange={(e) => set("past_experience", e.target.value)} data-testid="vo-experience" /></Field>
          <Field label="References (clients we can contact)">
            <Textarea rows={2} value={form.references} onChange={(e) => set("references", e.target.value)} data-testid="vo-refs" />
          </Field>
        </FormCard>

        {/* Documents */}
        <FormCard title="Supporting documents">
          <p className="text-xs text-slate-500 -mt-2 mb-2">Attach licenses, certificates, tax clearance, compliance documents. Max {meta.max_doc_count || 5} files, 5 MB each.</p>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-[#1E5CBA] hover:bg-[#1E5CBA]/5 transition" data-testid="vo-doc-dropzone">
            <FileArrowUp size={20} className="text-slate-400" />
            <span className="text-sm text-slate-600">
              {docs.length >= (meta.max_doc_count || 5) ? "Max files reached" : "Click to choose files (PDF, images, Word)"}
            </span>
            <input type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
              onChange={(e) => handleFiles(e.target.files)}
              disabled={docs.length >= (meta.max_doc_count || 5)} />
          </label>
          {docs.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="vo-doc-list">
              {docs.map((d, i) => (
                <li key={i} className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <FileArrowUp size={14} className="text-slate-400" />
                  <span className="flex-1 truncate">{d.name}</span>
                  <span className="text-[11px] text-slate-500 tabular-nums">{Math.round((d.size_bytes || 0) / 1024)} KB</span>
                  <button type="button" onClick={() => removeDoc(i)} className="text-rose-500 hover:text-rose-700" aria-label="Remove">
                    <Trash size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </FormCard>

        {/* Declaration */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <Checkbox checked={form.declaration_accepted} onCheckedChange={(v) => set("declaration_accepted", !!v)} data-testid="vo-declaration" />
            <span>I declare that the information and documents submitted are accurate and complete. I authorise DESCON Group to verify these details with relevant parties.</span>
          </label>
        </div>

        <div className="flex justify-end pb-12">
          <Button
            onClick={submit} disabled={busy || !form.declaration_accepted}
            className="btn-primary-blue px-6 py-3"
            data-testid="vo-submit-btn"
          >
            {busy ? "Submitting…" : "Submit registration"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}

function FormCard({ title, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
      <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600 mb-1 block">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
